import React, { lazy, Suspense } from 'react';
import { Switch, Redirect, type RouteComponentProps } from 'react-router-dom';
import { CompatRoute as Route } from 'react-router-dom-v5-compat';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PageLoader } from 'common-components/loading/PageLoader';

// Keep these as eager imports – they're lightweight non-view modules needed
// synchronously by the routing logic.
import { PageContainer } from 'layouts/page-container';
import { useNavBarItems } from 'layouts/app-container/navbar/use-navbar-sections';
import { PaymentListFilterKey } from 'views/billings/payments/components/payListFilterConfig';

// ---------------------------------------------------------------------------
// Lazy-loaded views
// Each view is split into its own chunk and only fetched when first navigated
// to, keeping the initial bundle small.
// ---------------------------------------------------------------------------

// Services
const GroupServiceDetailsView = lazy(() => import('views/group-services/service-details/GroupServiceDetailsView'));
const GroupServiceSessionView = lazy(() => import('views/group-services/session-details/GroupServiceSessionView'));
const PrintSessionRosterView = lazy(
  () => import('views/group-services/session-details/print-rosters/PrintSessionRosterView'),
);
const ServiceLandingView = lazy(() => import('views/services/ServiceLandingView'));
const ServiceDetailsView = lazy(() => import('views/services/details/ServiceDetailsView'));
const GroupSessionsListView = lazy(() => import('views/group-services/session-listings/GroupSessionsListingView'));
const SilServiceView = lazy(() =>
  import('views/sil-service/sil-service-view').then((m) => ({ default: m.SilServiceView })),
);

// Team
const InviteTeamMember = lazy(() => import('views/team-v2/TeamViewV2'));
const TeamListingViewV2 = lazy(() =>
  import('views/team/TeamListingViewV2').then((m) => ({ default: m.TeamListingViewV2 })),
);
const TeamAllViewsV2 = lazy(() =>
  import('views/team/all-views/TeamAllViewsV2').then((m) => ({
    default: m.default as unknown as React.ComponentType<any>,
  })),
);
const TeamDetailsView = lazy(() =>
  import('views/team/details/TeamDetailsView').then((m) => ({ default: m.TeamDetailsView })),
);
const ConnectedInsightsTableView = lazy(() =>
  import('views/insights/components/insights-table-view/insights-table-view').then((m) => ({
    default: m.ConnectedInsightsTableView,
  })),
);

// Bookings
const BookingsView = lazy(() => import('views/bookings/bookings-view').then((m) => ({ default: m.BookingsView })));
const BookingAllViews = lazy(() => import('../views/bookings/BookingViews/BookingAllViews'));
const BookingApproveView = lazy(() => import('views/bookings/BookingApproveView'));
const BookingDetailsViewV2 = lazy(() => import('../views/bookings/details/BookingDetailsViewV2'));
// Customers
const CustomerLandingView = lazy(() => import('views/customers/CustomerLandingView'));
const CustomerDetailPanel = lazy(() =>
  import('views/customers/details').then((m) => ({ default: m.CustomerDetailPanel })),
);
const CustomerAllViews = lazy(() =>
  import('../views/customers/CustomerViews/CustomerAllViews').then((m) => ({
    default: m.default as unknown as React.ComponentType<any>,
  })),
);
const ServiceAgreementService = lazy(() =>
  import('../views/service-agreements/pages/service-agreement-service').then((m) => ({
    default: m.ServiceAgreementService,
  })),
);
const BudgetDetail = lazy(() => import('../views/budgets').then((m) => ({ default: m.Detail })));

// Notifications
const NotificationsLandingView = lazy(() => import('views/notifications'));

// Billing
const PayListBatchDetailsView = lazy(() => import('../views/billings/BatchDetailView'));
const PaymentListingView = lazy(() =>
  import('../views/billings/PaymentListingView').then((m) => ({ default: m.PaymentListingView })),
);
const PDFPreviewView = lazy(() => import('../views/pdf-viewer/PDFPreviewView'));

// Invite users
const InviteUsersLandingView = lazy(() => import('views/invite-users/InviteUsersLandingView'));
const PendingInvitationListingView = lazy(() => import('views/invite-users/listing/PendingInvitationListingView'));

// Messaging
const MessagingLandingView = lazy(() => import('views/messaging/MessagingLandingView'));
const RedirectToView = lazy(() => import('views/redirect-page/RedirectToView'));

// Account management
const AccountAddMemberView = lazy(() => import('views/account-management/AccountAddMemberView'));
const AccountExportDataView = lazy(() => import('views/account-management/AccountExportDataView'));
const AccountLandingView = lazy(() => import('views/account-management/AccountLandingView'));
const BrandingView = lazy(() => import('views/account-management/BrandingView'));
const CompanyDetailsView = lazy(() =>
  import('views/account-management/CompanyDetailsView/CompanyDetailsView').then((m) => ({
    default: m.CompanyDetailsView,
  })),
);
const DebtorDetailsView = lazy(() => import('views/account-management/DebtorDetailsView'));
const DebtorManagementView = lazy(() => import('views/account-management/DebtorManagementView'));
const FeatureConfigurationView = lazy(() => import('views/account-management/FeatureConfigurationView'));
const FinanceFileExportConfigurationView = lazy(
  () => import('views/account-management/FinanceFileExportConfigurationView'),
);
const FundingAndPaymentView = lazy(() => import('views/account-management/FundingAndPaymentView'));
const ServiceDepartmentsView = lazy(() => import('views/account-management/ServiceDepartmentsView'));
const TeamManagementView = lazy(() =>
  import('views/account-management/team-management-view/team-management-view').then((m) => ({
    default: m.TeamManagementView,
  })),
);
const WorkflowTemplatesListView = lazy(() => import('views/account-management/WorkflowTemplatesListView'));
const FormsManagementView = lazy(() => import('views/account-management/forms/FormsManagementView'));
const CustomRoleTemplatesListView = lazy(
  () => import('views/account-management/custom-role-templates/CustomRoleTemplatesListView'),
);
const CreateWorkflowTemplate = lazy(() =>
  import('../views/account-management/WorkflowTemplatesCreateView').then((m) => ({
    default: m.CreateWorkflowTemplate,
  })),
);
const AccountImportDataView = lazy(() =>
  import('views/account-management/imports').then((m) => ({ default: m.AccountImportDataView })),
);

const AccountNewImportView = lazy(() =>
  import('views/account-management/imports/new').then((m) => ({ default: m.AccountNewImportView })),
);
const AccountShowImportView = lazy(() =>
  import('views/account-management/imports/show').then((m) => ({ default: m.AccountShowImportView })),
);
const ServiceAgreementPaymentMethodsView = lazy(
  () => import('views/account-management/ServiceAgreementPaymentMethodsView'),
);
const ServiceAgreementTemplatesView = lazy(() => import('views/account-management/ServiceAgreementTemplatesView'));
const TimeBasedAlertsConfigureView = lazy(() => import('views/account-management/TimeBasedAlertsConfigureView'));
const WorkLeaveTypesConfigurationView = lazy(
  () => import('views/account-management/WorkLeaveTypesConfigurationView/index'),
);
const PayPeriodConfigurationView = lazy(() =>
  import('views/account-management/PayPeriodConfig').then((m) => ({ default: m.PayPeriodConfigurationView })),
);
const SubscriptionManagementView = lazy(
  () => import('views/account-management/subscription-management/SubscriptionManagementView'),
);

// Account routes (profile / personal settings)
const AccountRoutes = lazy(() => import('views/account/routes').then((m) => ({ default: m.AccountRoutes })));

// Form builder
const FormBuilder = lazy(() => import('views/form-builder/FormBuilder'));
const FormDetailsView = lazy(() => import('views/form-builder/components/details/FormDetailsView'));
const SelectFormTemplateView = lazy(() => import('views/form-builder/SelectFormTemplateView'));
const SelectFormTypeView = lazy(() => import('views/form-builder/SelectFormTypeView'));

// Workflows
const WorkflowListingView = lazy(() => import('views/workflows/listing/WorkflowListingView'));
const WorkflowDetailsView = lazy(() => import('views/workflows/manage-workflow/details/WorkflowDetailsView'));
const WorkflowTemplateDetailView = lazy(
  () => import('views/workflows/workflow-templates/view-edit/WorkflowTemplateDetailView'),
);

// Access denied
const AccessDeniedView = lazy(() => import('views/exceptions/AccessDeniedView'));

// WIP / dev utilities
const TestPermissionView = lazy(() => import('views/wip/TestPermissionView'));
const TestTrpcView = lazy(() => import('views/wip/test-trpc-view').then((m) => ({ default: m.TestTrpcView })));
const TestMantineView = lazy(() => import('views/wip/test-mantine-view').then((m) => ({ default: m.TestMantineView })));

// ---------------------------------------------------------------------------

export const AuthorizedRoutes: React.FC = () => {
  const { boom284WorkforceInsightsTablePage, pinc1511SilService } = useFlags();
  const navItems = useNavBarItems();
  const landingLink = navItems[0]?.link ?? '/bookings';

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Redirect exact from='/home' to={landingLink} />

        {/* Messaging */}
        <Route path='/messaging' component={MessagingLandingView} />

        {/* Billings */}
        <Route
          path='/payments/processed'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.PROCESSED} />
          )}
        />
        <Route
          path='/payments/paid'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.PAID} />
          )}
        />
        <Route
          path='/payments/waiting-to-be-processed'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.WAITING_TO_BE_PROCESSED} />
          )}
        />
        <Route
          path='/payments/batches'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.BATCH} />
          )}
        />
        <Route
          path='/payments/non-billable'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.NON_BILLABLE} />
          )}
        />
        <Route
          path='/payments/rejected'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.REJECTED} />
          )}
        />
        <Route
          path='/payments/waived'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.WAIVED} />
          )}
        />
        <Route
          path='/payments/timesheets'
          render={(props: RouteComponentProps) => (
            <PaymentListingView {...props} selectedFilterKey={PaymentListFilterKey.SHIFT_HOURS} />
          )}
        />

        <Route exact path='/group-service/details/:serviceId' component={GroupServiceDetailsView} />

        <Route
          exact
          path='/group-service/:serviceId/session/details/:serviceDateTimeId'
          component={GroupServiceSessionView}
        />

        <Route
          exact
          path='/group-service/:serviceId/session/details/:serviceDateTimeId/print-roster'
          component={PrintSessionRosterView}
        />

        <Route exact path='/bookings' component={BookingsView} />

        <Route path='/customer/details/:userId' component={CustomerDetailPanel} />

        <PageContainer>
          {/* Services */}
          <Route path='/services' component={ServiceLandingView} />
          {/* /service/details/:serviceId detail page for support service and support coordination service */}
          <Route exact path='/service/details/:serviceId' component={ServiceDetailsView} />

          {pinc1511SilService && <Route path='/service/sil/:serviceId/:tabName' component={SilServiceView} />}

          {/* Session listings for group services */}
          <Route path='/group-service/sessions' component={GroupSessionsListView} />

          {/* Teams */}
          <Route exact path='/team' component={TeamListingViewV2} />
          <Route exact path='/team/invite-member' component={InviteTeamMember} />
          <Route exact path='/team/details/:workerId' component={TeamDetailsView} />
          <Route exact path='/team/all-views' component={TeamAllViewsV2} />
          {boom284WorkforceInsightsTablePage && (
            <Route exact path='/team/team-insights' component={ConnectedInsightsTableView} />
          )}

          {/* Bookings */}
          {/* TODO? */}
          <Route exact path='/bookings-approve' component={BookingApproveView} />

          <Route path='/bookings/all-views' component={BookingAllViews} />

          {/*<Route exact path={'/bookings/details/:bookingId'} component={BookingActualView} />*/}
          <Route exact path='/bookings/details/:bookingId' component={BookingDetailsViewV2} />

          <Route exact path='/batch/details/:batchId' component={PayListBatchDetailsView} />

          {/* Customers */}
          <Route exact path='/customers' component={CustomerLandingView} />

          <Route
            exact
            path='/customers/:customerId/service-agreements/:serviceAgreementId/service-agreement-service/:serviceAgreementServiceId'
            component={ServiceAgreementService}
          />
          <Route
            exact
            path='/customers/:customerId/service-agreements/:serviceAgreementId/budget/:serviceId'
            component={BudgetDetail}
          />

          {/* Customer Views */}
          <Route exact path='/customers/all-views' component={CustomerAllViews} />

          {/* Notifications */}
          <Route path='/notifications' component={NotificationsLandingView} />

          {/* Invite users */}
          <Route path='/invite-users' component={InviteUsersLandingView} />
          <Route path='/pending-invitations' component={PendingInvitationListingView} />

          {/* Profile */}
          <AccountRoutes />
          <Route path='/company' component={CompanyDetailsView} />

          <Route path='/pdf' component={PDFPreviewView} />

          <Route path='/redirect' component={RedirectToView} />

          {/*   Added by Jir : Account Management */}
          {/* TODO : Change to a better url maybe */}
          <Route path='/account/landing' component={AccountLandingView} />

          {/* Account management */}
          <Route path='/account/add-user' component={AccountAddMemberView} />
          <Route path='/account/service-department' component={ServiceDepartmentsView} />
          <Route path='/account/team-management' component={TeamManagementView} />
          <Route path='/account/funding-and-payment' component={FundingAndPaymentView} />
          <Route path='/account/testPermissions' component={TestPermissionView} />
          <Route path='/account/branding' component={BrandingView} />
          <Route exact path='/account/debtor-management' component={DebtorManagementView} />
          <Route exact path='/account/debtor-management/:debtorId' component={DebtorDetailsView} />
          <Route exact path='/account/workflows' component={WorkflowTemplatesListView} />
          <Route exact path='/account/role-templates' component={CustomRoleTemplatesListView} />
          <Switch>
            <Route exact path='/account/workflows/create' component={CreateWorkflowTemplate} />
            <Route path='/account/workflows/:workflowTemplateId' component={WorkflowTemplateDetailView} />
          </Switch>
          <Route
            path='/account/features/service-agreement-templates/:paymentMethod'
            component={ServiceAgreementTemplatesView}
          />
          <Route
            exact
            path='/account/features/service-agreement-templates'
            component={ServiceAgreementPaymentMethodsView}
          />
          <Route
            path='/account/features/service-time-based-alerts-configure'
            component={TimeBasedAlertsConfigureView}
          />
          <Route
            exact
            path='/account/features/finance-file-export-configuration'
            component={FinanceFileExportConfigurationView}
          />
          <Route
            path='/account/features/finance-file-export-configuration/work-types-leave-types'
            component={WorkLeaveTypesConfigurationView}
          />
          <Route
            path='/account/features/finance-file-export-configuration/pay-period'
            component={PayPeriodConfigurationView}
          />
          <Route exact path='/account/features' component={FeatureConfigurationView} />
          <Route path='/account/export-data' component={AccountExportDataView} />

          <Switch>
            <Route exact path='/account/imports' component={AccountImportDataView} />
            <Route path='/account/imports/new' component={AccountNewImportView} />
            <Route path='/account/imports/:jobId' component={AccountShowImportView} />
          </Switch>

          <Route exact path='/account/forms' component={FormsManagementView} />
          <Route exact path='/account/forms/newform' component={SelectFormTypeView} />
          <Route exact path='/account/forms/templates/:formType' component={SelectFormTemplateView} />
          <Route exact path='/account/form/:formId' component={FormDetailsView} />
          <Route path='/account/forms/create' component={FormBuilder} />
          <Route path='/account/forms/copy/:formId' component={FormBuilder} />
          <Route path='/account/forms/edit/:formId' component={FormBuilder} />
          <Route path='/account/subscription-management' component={SubscriptionManagementView} />

          {/* Workflows */}
          <Route exact path='/workflows' component={WorkflowListingView} />
          <Route exact path='/workflows/details/:workflowId' component={WorkflowDetailsView} />

          <Route exact path='/trpc/test' component={TestTrpcView} />
          <Route exact path='/mantine/test' component={TestMantineView} />

          {/* Access denied */}
          <Route path='/access-denied' component={AccessDeniedView} />
        </PageContainer>
      </Switch>
    </Suspense>
  );
};
